/**
 * Focal theme: custom JavaScript.
 * (The original developer documentation comment was removed from this file; it is in the Focal theme docs.)
 *
 * ETHIQUE: Product card video
 * Desktop (mouse): when the mouse is over a product card, the first video of that product plays on top of the
 * image (muted, looping). Nothing loads until the card is hovered.
 * Touch (phone / tablet): the video plays while at least 60% of the card is on screen and stops when it scrolls
 * away. Skipped when the visitor asked for reduced motion or data saving.
 * The video URL comes from the card's data-ethique-video attribute (snippets/product-item.liquid); cards without it
 * fall back to the product's .js endpoint. Products without a video are unaffected.
 */
(function () {
  if (!window.matchMedia) return;

  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  if (!canHover && (reduceMotion || saveData)) return;

  var cache = {};

  function fetchVideoUrl(path) {
    if (cache[path]) return cache[path];

    cache[path] = fetch(path + '.js')
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (product) {
        if (!product || !product.media) return null;

        var video = null;
        for (var i = 0; i < product.media.length; i++) {
          if (product.media[i].media_type === 'video') { video = product.media[i]; break; }
        }
        if (!video || !video.sources) return null;

        var mp4s = video.sources.filter(function (s) {
          return s.format === 'mp4' || (s.mime_type || '').indexOf('mp4') > -1;
        });
        if (!mp4s.length) return null;

        mp4s.sort(function (a, b) { return (a.height || 0) - (b.height || 0); });
        var pick = mp4s[0];
        for (var j = 0; j < mp4s.length; j++) {
          if ((mp4s[j].height || 0) <= 720) pick = mp4s[j];
        }
        return pick.url;
      })
      .catch(function () { return null; });

    return cache[path];
  }

  function getVideoUrl(card, link) {
    if (card.hasAttribute('data-ethique-video')) {
      return Promise.resolve(card.getAttribute('data-ethique-video') || null);
    }
    return fetchVideoUrl(link.pathname);
  }

  function start(card) {
    if (card.__ethiqueActive) return;
    card.__ethiqueActive = true;

    var link = card.querySelector('.product-item__aspect-ratio');
    if (!link || !link.pathname) return;

    getVideoUrl(card, link).then(function (url) {
      if (!url || !card.__ethiqueActive) return;

      var video = link.querySelector('.ethique-hover-video');
      if (!video) {
        video = document.createElement('video');
        video.className = 'ethique-hover-video';
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = canHover ? 'auto' : 'metadata';
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('disablepictureinpicture', '');
        video.setAttribute('aria-hidden', 'true');
        video.src = url;
        video.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;opacity:0;transition:opacity .3s ease;z-index:1;pointer-events:none;';
        video.addEventListener('playing', function () {
          if (card.__ethiqueActive) video.style.opacity = '1';
        });
        link.appendChild(video);
      }

      var playing = video.play();
      if (playing && playing.catch) playing.catch(function () {});
    });
  }

  function stop(card) {
    card.__ethiqueActive = false;
    var video = card.querySelector('.ethique-hover-video');
    if (!video) return;
    video.pause();
    video.style.opacity = '0';
    if (canHover) {
      try { video.currentTime = 0; } catch (e) {}
    }
  }

  if (canHover) {
    document.addEventListener('mouseover', function (event) {
      var card = event.target.closest ? event.target.closest('product-item') : null;
      if (card) start(card);
    });

    document.addEventListener('mouseout', function (event) {
      var card = event.target.closest ? event.target.closest('product-item') : null;
      if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
      stop(card);
    });
    return;
  }

  // Touch devices: play while the card is visible. Only cards that have a video are observed.
  if (!('IntersectionObserver' in window)) return;

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.6) start(entry.target);
      else stop(entry.target);
    });
  }, { threshold: [0, 0.6] });

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    var cards = root.matches && root.matches('product-item[data-ethique-video]') ? [root] : [];
    cards = cards.concat(Array.prototype.slice.call(root.querySelectorAll('product-item[data-ethique-video]')));
    cards.forEach(function (card) {
      if (card.__ethiqueObserved || !card.getAttribute('data-ethique-video')) return;
      card.__ethiqueObserved = true;
      observer.observe(card);
    });
  }

  function init() {
    scan(document);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        for (var i = 0; i < m.addedNodes.length; i++) scan(m.addedNodes[i]);
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Pause everything when the tab goes to the background; when it comes back, re-observe so visible cards play again.
  document.addEventListener('visibilitychange', function () {
    Array.prototype.forEach.call(document.querySelectorAll('product-item[data-ethique-video]'), function (card) {
      if (!card.__ethiqueObserved) return;
      if (document.hidden) {
        stop(card);
      } else {
        observer.unobserve(card);
        observer.observe(card);
      }
    });
  });
})();
